import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, Switch, CircularProgress, Paper } from '@mui/material';
import { fetchTwoFactorConfig, updateTwoFactorConfig } from '~/redux/actions/two-factor-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import OlcButton from '~/components/General/OlcButton';
import './styles.scss';

const SecuritySettings = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ systemAdmins: true, normalUsers: false });

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      const result = await dispatch(fetchTwoFactorConfig());
      if (result?.message === 'success') {
        setConfig(result.data);
      }
      setLoading(false);
    };
    loadConfig();
  }, [dispatch]);

  const handleSave = async () => {
    setSaving(true);
    const result = await dispatch(updateTwoFactorConfig(config));
    setSaving(false);
    if (result?.message === 'success') {
      dispatch(success('Two-factor authentication policy updated'));
    } else {
      dispatch(failure(result?.response?.data?.message || 'Failed to update settings'));
    }
  };

  if (loading) {
    return (
      <Box className="securitySettingsPage">
        <Box className="securitySettingsLoader">
          <CircularProgress sx={{ color: '#ed5c2f' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box className="securitySettingsPage">
      <Box className="securitySettingsHeader">
        <Typography variant="h5">Security Settings</Typography>
        <Typography variant="body2" className="securitySettingsSubtext">
          Configure two-factor authentication requirements for different user types.
        </Typography>
      </Box>

      <Paper className="securitySettingsCard" elevation={0}>
        <Typography variant="h6" className="securitySettingsCardTitle">
          Two-Factor Authentication
        </Typography>

        <Box className="securitySettingsRow">
          <Box className="securitySettingsRowInfo">
            <Typography variant="body1" className="securitySettingsRowTitle">
              System Admins
            </Typography>
            <Typography variant="body2" className="securitySettingsRowDesc">
              Require two-factor authentication for all system administrator accounts.
            </Typography>
          </Box>
          <Switch
            checked={config.systemAdmins}
            onChange={(e) => setConfig({ ...config, systemAdmins: e.target.checked })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed5c2f' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#ed5c2f',
              },
            }}
          />
        </Box>

        <Box className="securitySettingsDivider" />

        <Box className="securitySettingsRow">
          <Box className="securitySettingsRowInfo">
            <Typography variant="body1" className="securitySettingsRowTitle">
              Normal Users
            </Typography>
            <Typography variant="body2" className="securitySettingsRowDesc">
              Require two-factor authentication for all regular user accounts.
            </Typography>
          </Box>
          <Switch
            checked={config.normalUsers}
            onChange={(e) => setConfig({ ...config, normalUsers: e.target.checked })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed5c2f' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#ed5c2f',
              },
            }}
          />
        </Box>
      </Paper>

      <Box className="securitySettingsActions">
        <OlcButton
          variant="primary"
          icon={false}
          className="securitySettingsSaveBtn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <CircularProgress
              sx={{ color: 'white', width: '22px !important', height: '22px !important' }}
            />
          ) : (
            'Save Changes'
          )}
        </OlcButton>
      </Box>
    </Box>
  );
};

export default SecuritySettings;
